/**
 * Push Subscription Service
 * Handles service worker registration and push notification subscriptions
 * 
 * Phase 2: Service Worker + Web Push API
 */

import { auth } from '@/config/firebase';

// API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
// Remove trailing /api if present since we'll add specific endpoints
const API_ROOT = API_BASE_URL.replace(/\/api$/, '');

// Debug logging - only in dev with VITE_DEBUG_PUSH=true
const DEBUG_PUSH = import.meta.env.DEV && import.meta.env.VITE_DEBUG_PUSH === 'true';

// VAPID public key - should match the private key on the server
// Generate with: npx web-push generate-vapid-keys
// Store private key in backend env: VAPID_PRIVATE_KEY
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

// Check if service workers and push are supported
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

// Check if service worker is registered
export const isServiceWorkerRegistered = async () => {
  if (!isPushSupported()) return false;
  
  const registration = await navigator.serviceWorker.getRegistration('/sw.js');
  return !!registration;
};

// Register the service worker
export const registerServiceWorker = async () => {
  if (!isPushSupported()) {
    console.warn('[Push] Service workers not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });

    if (DEBUG_PUSH) console.log('[Push] Service worker registered:', registration.scope);

    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;
    if (DEBUG_PUSH) console.log('[Push] Service worker is ready');

    return registration;
  } catch (error) {
    console.error('[Push] Service worker registration failed:', error);
    return null;
  }
};

// Get existing push subscription
export const getExistingSubscription = async () => {
  if (!isPushSupported()) return null;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription;
  } catch (error) {
    console.error('[Push] Error getting subscription:', error);
    return null;
  }
};

// Subscribe to push notifications
export const subscribeToPush = async () => {
  if (!isPushSupported()) {
    console.warn('[Push] Service worker / Push not supported in this browser');
    return null;
  }

  // If VAPID key is missing in prod, fall back to in-tab notifications instead of throwing
  if (!VAPID_PUBLIC_KEY) {
    console.warn('[Push] VAPID_PUBLIC_KEY not configured; skipping push subscription');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;

    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      if (DEBUG_PUSH) console.log('[Push] Already subscribed locally, ensuring server sync...');
      // Still save to server in case it wasn't saved before
      await saveSubscriptionToServer(subscription);
      return subscription;
    }

    // Convert VAPID key to Uint8Array
    const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);

    // Subscribe to push
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true, // Required - ensures notifications are visible
      applicationServerKey
    });

    if (DEBUG_PUSH) console.log('[Push] Subscribed successfully');

    // Send subscription to backend
    await saveSubscriptionToServer(subscription);

    return subscription;
  } catch (error) {
    console.error('[Push] Subscription failed:', error);
    // In local dev without backend, avoid breaking the app. Return null so UI can still use foreground notifications.
    return null;
  }
};

// Unsubscribe from push notifications
export const unsubscribeFromPush = async () => {
  try {
    const subscription = await getExistingSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      await removeSubscriptionFromServer(subscription);
      if (DEBUG_PUSH) console.log('[Push] Unsubscribed successfully');
    }
    
    return true;
  } catch (error) {
    console.error('[Push] Unsubscribe failed:', error);
    return false;
  }
};

// Save subscription to backend
const saveSubscriptionToServer = async (subscription) => {
  const user = auth.currentUser;
  if (!user) {
    console.warn('[Push] No user logged in, cannot save subscription');
    return;
  }

  const token = await user.getIdToken();
  
  try {
    const response = await fetch(`${API_ROOT}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        subscription: subscription.toJSON(),
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      })
    });

    if (!response.ok) {
      throw new Error('Failed to save subscription');
    }

    if (DEBUG_PUSH) console.log('[Push] Subscription saved to server');
  } catch (error) {
    console.error('[Push] Error saving subscription:', error);
    // Don't throw - subscription is still valid locally
  }
};

// Remove subscription from backend
const removeSubscriptionFromServer = async (subscription) => {
  const user = auth.currentUser;
  if (!user) return;

  const token = await user.getIdToken();
  
  try {
    await fetch(`${API_ROOT}/api/push/unsubscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint
      })
    });

    if (DEBUG_PUSH) console.log('[Push] Subscription removed from server');
  } catch (error) {
    console.error('[Push] Error removing subscription:', error);
  }
};

// Send message to service worker
export const sendMessageToSW = async (message) => {
  if (!isPushSupported()) return;

  const registration = await navigator.serviceWorker.ready;
  
  if (registration.active) {
    registration.active.postMessage(message);
  }
};

// Listen for messages from service worker
export const onSWMessage = (callback) => {
  if (!isPushSupported()) return () => {};

  const handler = (event) => {
    callback(event.data);
  };

  navigator.serviceWorker.addEventListener('message', handler);
  
  return () => {
    navigator.serviceWorker.removeEventListener('message', handler);
  };
};

// Helper: Convert base64 VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

// Get push permission status
export const getPushPermissionStatus = () => {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
};

// Check if push is fully enabled (permission granted AND subscribed)
export const isPushEnabled = async () => {
  if (!isPushSupported()) return false;
  if (Notification.permission !== 'granted') return false;
  
  const subscription = await getExistingSubscription();
  return !!subscription;
};

export default {
  isPushSupported,
  isServiceWorkerRegistered,
  registerServiceWorker,
  getExistingSubscription,
  subscribeToPush,
  unsubscribeFromPush,
  sendMessageToSW,
  onSWMessage,
  getPushPermissionStatus,
  isPushEnabled
};
